/*
 * The Landing Page editor's state, shared by the classic panel and the v3 screen.
 *
 * The one piece of real thinking here is `problems`. The frozen frame (`80:286`) shows a save being
 * refused with the reasons listed and each one jumping to its field, which the shipped panel never
 * did — it sent the object and let the server's 400 arrive as one opaque sentence.
 *
 * So the limits below are the server's, copied from `contentSchema` in `server/src/routes/admin.ts`
 * and nothing else. A client that refuses a save the server would have accepted is worse than one
 * that does not check at all: it invents a rule nobody wrote, on a page an organizer is trying to
 * publish. Anything the server tolerates but a reader would trip over is a `warning` instead, which
 * is shown and does not block.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, adminApi } from '../../api';
import type { ComingSoonDto, ContentDto, ContentSection } from '../../api';

/** Mirrors `contentSchema`. Changing one of these without changing the server is a bug. */
export const LIMITS = {
  name: 120,
  tagline: 200,
  earlyBirdNote: 300,
  ticketsTagline: 80,
  contactEmail: 160,
  company: 160,
  facts: 12,
  lineup: 60,
  sections: 20,
  comingSoonHeadline: 80,
  comingSoonEyebrow: 120,
  comingSoonMessage: 600,
  comingSoonCtaLabel: 40,
} as const;

export type LandingSectionKey = 'basics' | 'hero' | 'tickets' | 'facts' | 'lineup' | 'sections';

export interface LandingProblem {
  /** Which editor section to open when this is chosen. */
  section: LandingSectionKey;
  field: string;
  message: string;
  /** `error` refuses the save because the server would too. `warning` is advice. */
  kind: 'error' | 'warning';
}

function problemsOf(content: ContentDto | null): LandingProblem[] {
  if (!content) return [];
  const out: LandingProblem[] = [];
  const tooLong = (value: string, limit: number, section: LandingSectionKey, field: string, label: string) => {
    if (value.length > limit) {
      out.push({ section, field, kind: 'error', message: `${label} เกิน ${value.length - limit} ตัวอักษร — ตัดให้เหลือไม่เกิน ${limit}` });
    }
  };

  if (!content.name.trim()) out.push({ section: 'basics', field: 'name', kind: 'error', message: 'ต้องมีชื่องาน' });
  if (!content.tagline.trim()) out.push({ section: 'basics', field: 'tagline', kind: 'error', message: 'ต้องมี Tagline' });
  tooLong(content.name, LIMITS.name, 'basics', 'name', 'ชื่องาน');
  tooLong(content.tagline, LIMITS.tagline, 'basics', 'tagline', 'Tagline');
  tooLong(content.earlyBirdNote, LIMITS.earlyBirdNote, 'basics', 'earlyBirdNote', 'ข้อความ Early Bird');
  tooLong(content.contactEmail, LIMITS.contactEmail, 'basics', 'contactEmail', 'อีเมลติดต่อ');
  tooLong(content.company, LIMITS.company, 'basics', 'company', 'ชื่อบริษัท');
  tooLong(content.ticketsTagline, LIMITS.ticketsTagline, 'tickets', 'ticketsTagline', 'Tagline หมวดตั๋ว');

  // A warning, not an error: the server accepts any string up to 160 here, and refusing a save the
  // server would have taken is not this screen's business. It still says so, because the address
  // ends up in the footer of a public page.
  if (content.contactEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(content.contactEmail.trim())) {
    out.push({ section: 'basics', field: 'contactEmail', kind: 'warning', message: 'อีเมลติดต่อดูไม่ครบรูปแบบ name@domain.com — ผู้ซื้อจะติดต่อกลับไม่ได้' });
  }
  if (!content.heroImageKey) {
    out.push({ section: 'hero', field: 'heroImageKey', kind: 'warning', message: 'ยังไม่มีรูป Hero — หน้าเว็บจะแสดงพื้นหลังเปล่า' });
  }
  if (content.facts.length > LIMITS.facts) {
    out.push({ section: 'facts', field: 'facts', kind: 'error', message: `Event facts มีได้ไม่เกิน ${LIMITS.facts} รายการ` });
  }
  if (content.lineup.length > LIMITS.lineup) {
    out.push({ section: 'lineup', field: 'lineup', kind: 'error', message: `Lineup มีได้ไม่เกิน ${LIMITS.lineup} รายชื่อ` });
  }
  const noPhoto = content.lineup.filter((a) => a.name.trim() && !a.photoKey).length;
  if (noPhoto > 0) {
    out.push({ section: 'lineup', field: 'lineup', kind: 'warning', message: `ศิลปิน ${noPhoto} รายการยังไม่มีรูป — หน้าเว็บจะแสดง placeholder จนกว่าจะอัปโหลด` });
  }
  if (content.sections.length > LIMITS.sections) {
    out.push({ section: 'sections', field: 'sections', kind: 'error', message: `Section เพิ่มเติมมีได้ไม่เกิน ${LIMITS.sections}` });
  }
  return out;
}

export interface LandingState {
  content: ContentDto | null;
  /** The values as last saved, for the dirty comparison and for discarding. */
  saved: ContentDto | null;
  loading: boolean;
  loadError: string;
  saving: boolean;
  justSaved: boolean;
  saveError: string;
  dirty: boolean;
  problems: LandingProblem[];
  blocking: LandingProblem[];
  set: <K extends keyof ContentDto>(key: K, value: ContentDto[K]) => void;
  setComingSoon: <K extends keyof ComingSoonDto>(key: K, value: ComingSoonDto[K]) => void;
  moveArtist: (index: number, delta: number) => void;
  moveFact: (from: number, to: number) => void;
  addSection: (type: 'faq' | 'sponsors') => void;
  save: () => Promise<void>;
  discard: () => void;
}

const EMPTY_COMING_SOON: ComingSoonDto = {
  headline: '', eyebrow: '', message: '', ctaLabel: '', ctaUrl: '', showHero: true,
};

export function useLandingContent(token: string, eventSlug?: string): LandingState {
  const [content, setContent] = useState<ContentDto | null>(null);
  const [saved, setSaved] = useState<ContentDto | null>(null);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const next = await adminApi.getContent(token, eventSlug);
      setContent(next);
      setSaved(next);
      setLoadError('');
    } catch {
      setLoadError('โหลดเนื้อหาไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  useEffect(() => { load(); }, [load]);

  const set = useCallback(<K extends keyof ContentDto>(key: K, value: ContentDto[K]) => {
    setJustSaved(false);
    setContent((c) => (c ? { ...c, [key]: value } : c));
  }, []);

  const setComingSoon = useCallback(<K extends keyof ComingSoonDto>(key: K, value: ComingSoonDto[K]) => {
    setJustSaved(false);
    setContent((c) => (c ? { ...c, comingSoon: { ...(c.comingSoon ?? EMPTY_COMING_SOON), [key]: value } } : c));
  }, []);

  const problems = useMemo(() => problemsOf(content), [content]);
  const blocking = useMemo(() => problems.filter((p) => p.kind === 'error'), [problems]);
  const dirty = !!content && !!saved && JSON.stringify(content) !== JSON.stringify(saved);

  const save = useCallback(async () => {
    if (!content || blocking.length > 0) return;
    setSaving(true);
    setSaveError('');
    setJustSaved(false);
    try {
      // Stub rows created by "+ add" and never filled are dropped, or saving right after adding one
      // fails the server's own validation for a row the person never meant to create.
      const payload: ContentDto = {
        ...content,
        facts: content.facts.filter((f) => f.k.trim() || f.v.trim()),
        lineup: content.lineup.filter((a) => a.name.trim() || a.time.trim()),
        sections: content.sections.map((s) => s.type === 'faq'
          ? { ...s, items: s.items.filter((it) => it.q.trim() || it.a.trim()) }
          : { ...s, items: s.items.filter((it) => it.name.trim()) }),
      };
      const updated = await adminApi.updateContent(token, payload, eventSlug);
      setContent(updated);
      setSaved(updated);
      setJustSaved(true);
    } catch (e) {
      // Nothing is rolled back. A save that failed must leave every word the person wrote where
      // they wrote it, which is the whole reason the draft and the saved copy are separate.
      setSaveError(e instanceof ApiError ? e.message : 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  }, [content, blocking.length, token, eventSlug]);

  return {
    content, saved,
    loading: !content && !loadError,
    loadError, saving, justSaved, saveError,
    dirty, problems, blocking,
    set, setComingSoon, save,
    moveArtist: (index, delta) => {
      setContent((c) => {
        if (!c) return c;
        const target = index + delta;
        if (target < 0 || target >= c.lineup.length) return c;
        const next = [...c.lineup];
        [next[index], next[target]] = [next[target], next[index]];
        return { ...c, lineup: next };
      });
      setJustSaved(false);
    },
    moveFact: (from, to) => {
      setContent((c) => {
        if (!c || to < 0 || to >= c.facts.length || from === to) return c;
        const next = [...c.facts];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        return { ...c, facts: next };
      });
      setJustSaved(false);
    },
    addSection: (type) => {
      const section: ContentSection = type === 'faq'
        ? { id: crypto.randomUUID(), type: 'faq', title: 'คำถามที่พบบ่อย', items: [] }
        : { id: crypto.randomUUID(), type: 'sponsors', title: 'สปอนเซอร์', items: [] };
      setContent((c) => (c ? { ...c, sections: [...c.sections, section] } : c));
      setJustSaved(false);
    },
    discard: () => {
      setContent(saved);
      setSaveError('');
      setJustSaved(false);
    },
  };
}
